import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// ─── Permission Capabilities ──────────────────────────────────────────────────

export interface LabPermissions {
    /** Create & place new lab orders (admin, doctor, receptionist) */
    canCreateOrder: boolean;
    /** Enter and save test result parameters (admin, lab_technician, pathologist) */
    canEnterResults: boolean;
    /** Approve or reject submitted reports (admin, pathologist) */
    canApproveReports: boolean;
    /** Full CRUD on test catalog, sample types, test groups (admin only) */
    canManageCatalog: boolean;
    /** Register and update specimen vials (admin, lab_technician, nurse) */
    canCollectSamples: boolean;
    /** Upload & send report PDFs via email (admin, pathologist) */
    canDispatchReports: boolean;
    /** View all orders and reports (all authenticated users) */
    canViewOrders: boolean;
    /** Sign off a draft result before it's submitted for pathologist review (admin, lab_technician, pathologist) */
    canVerifyResults: boolean;
    /** Still resolving Cognito session */
    loading: boolean;
    /** Identity of the signed-in user, for stamping enteredBy/verifiedBy/criticalAcknowledgedBy fields. Null while loading or if identity can't be resolved. */
    currentUser: { id: string; name: string } | null;
}

// ─── Group Name Sets ──────────────────────────────────────────────────────────

const ADMIN_GROUPS       = ['admin', 'admins', 'administrator', 'administrators'];
const DOCTOR_GROUPS      = ['doctor', 'doctors', 'physician', 'physicians', 'provider', 'providers'];
const PATHOLOGIST_GROUPS = ['pathologist', 'pathologists', 'pathology'];
const TECHNICIAN_GROUPS  = ['lab_technician', 'lab-technician', 'technician', 'lab_tech', 'labtech'];
const RECEPTIONIST_GROUPS = ['receptionist', 'receptionists', 'front_desk', 'frontdesk'];
const NURSE_GROUPS       = ['nurse', 'nurses', 'staff', 'nursing'];

const matchesGroup = (groups: string[], set: string[]): boolean =>
    groups.some(g => set.includes(g.toLowerCase().trim()));

// ─── Hook ─────────────────────────────────────────────────────────────────────

const FAIL_OPEN: LabPermissions = {
    canCreateOrder:    true,
    canEnterResults:   true,
    canApproveReports: true,
    canManageCatalog:  true,
    canCollectSamples: true,
    canDispatchReports:true,
    canViewOrders:     true,
    canVerifyResults:  true,
    loading:           false,
    currentUser:       null,
};

export function useLabPermissions(): LabPermissions {
    const [permissions, setPermissions] = useState<LabPermissions>({ ...FAIL_OPEN, loading: true });

    useEffect(() => {
        let cancelled = false;

        const resolve = async () => {
            try {
                const session = await fetchAuthSession();
                const payload = session?.tokens?.idToken?.payload as any;
                const rawGroups: string[] = payload?.['cognito:groups'] ?? [];
                const currentUser = payload
                    ? {
                        id: String(payload?.sub ?? payload?.email ?? payload?.['cognito:username'] ?? ''),
                        name: String(payload?.name ?? payload?.email ?? payload?.['cognito:username'] ?? 'Unknown user'),
                    }
                    : null;

                if (cancelled) return;

                // No group assigned → fail-open (legacy users, single-tenant installs)
                if (!rawGroups.length) {
                    setPermissions({ ...FAIL_OPEN, loading: false, currentUser });
                    return;
                }

                const isAdmin       = matchesGroup(rawGroups, ADMIN_GROUPS);
                const isDoctor      = matchesGroup(rawGroups, DOCTOR_GROUPS);
                const isPathologist = matchesGroup(rawGroups, PATHOLOGIST_GROUPS);
                const isTechnician  = matchesGroup(rawGroups, TECHNICIAN_GROUPS);
                const isReceptionist = matchesGroup(rawGroups, RECEPTIONIST_GROUPS);
                const isNurse       = matchesGroup(rawGroups, NURSE_GROUPS);

                setPermissions({
                    canCreateOrder:     isAdmin || isDoctor || isReceptionist,
                    canEnterResults:    isAdmin || isTechnician || isPathologist,
                    canApproveReports:  isAdmin || isPathologist,
                    canManageCatalog:   isAdmin,
                    canCollectSamples:  isAdmin || isTechnician || isNurse,
                    canDispatchReports: isAdmin || isPathologist,
                    canViewOrders:      true, // all authenticated users
                    canVerifyResults:   isAdmin || isTechnician || isPathologist,
                    loading:            false,
                    currentUser,
                });
            } catch {
                // Auth error → fail-open (never block clinical workflows)
                if (!cancelled) setPermissions({ ...FAIL_OPEN, loading: false });
            }
        };

        resolve();
        return () => { cancelled = true; };
    }, []);

    return permissions;
}

export default useLabPermissions;
